<template>
  <MainLayout :initialDrawerExpanded="false" contentClass="pa-md-4" :has-sidebar="false">
    <template #header>
      <div class="overline">
        <PageBack />
      </div>
    </template>

    <template #content>
      <v-container fluid class="pa-0 pa-md-4">
        <v-row>
          <v-col cols="12" md="10" offset-md="1">

            <div v-if="loading" class="d-flex justify-center py-12">
              <v-progress-circular indeterminate color="primary" />
            </div>

            <v-alert v-else-if="error" type="error" class="mb-4">{{ error }}</v-alert>

            <template v-else-if="detail">

              <!-- Header -->
              <div class="d-flex align-center flex-wrap mb-5" style="gap: 12px">
                <v-avatar color="primary lighten-4" size="48">
                  <span class="subtitle-1 font-weight-medium primary--text">{{ initials }}</span>
                </v-avatar>
                <div>
                  <div class="subtitle-1 font-weight-medium">
                    {{ detail.username }}
                    <span v-if="fullName" class="grey--text font-weight-regular ml-1">— {{ fullName }}</span>
                  </div>
                  <div v-if="detail.organisation_name" class="d-flex align-center caption grey--text">
                    <v-avatar size="16" class="mr-1">
                      <v-img v-if="detail.organisation_icon" :src="detail.organisation_icon" />
                      <v-icon v-else x-small>mdi-domain</v-icon>
                    </v-avatar>
                    {{ detail.organisation_name }}
                  </div>
                </div>
                <v-spacer />
                <v-chip v-if="detail.is_staff" x-small color="primary" dark>Staff</v-chip>
                <v-chip v-if="detail.is_superuser" x-small color="deep-purple" dark>Superuser</v-chip>
                <v-chip v-if="detail.is_demo" x-small color="orange" dark>Démo</v-chip>
                <v-chip v-if="!detail.is_active" x-small color="error" dark>Inactif</v-chip>
              </div>

              <v-row align="start">
                <!-- Profile card -->
                <v-col cols="12" md="6">
                  <v-card elevation="0" class="mb-4 rounded-lg" outlined>
                    <v-card-title class="subtitle-1 font-weight-medium pb-1">
                      <v-icon left small>mdi-account-outline</v-icon>
                      Profil
                    </v-card-title>
                    <v-card-text style="display: grid; gap: 10px">
                      <div>
                        <div class="caption grey--text">Email</div>
                        <div class="body-2">{{ detail.email || '—' }}</div>
                      </div>
                      <div>
                        <div class="caption grey--text">Inscrit le</div>
                        <div class="body-2">{{ formatDate(detail.date_joined) }}</div>
                      </div>
                      <div>
                        <div class="caption grey--text">Dernière connexion</div>
                        <div class="body-2">{{ formatDate(detail.last_login) }}</div>
                      </div>
                      <div>
                        <div class="caption grey--text mb-1">Équipes</div>
                        <div v-if="detail.memberships.length" class="d-flex flex-wrap" style="gap: 6px">
                          <v-chip v-for="(m, i) in detail.memberships" :key="i" x-small outlined>
                            {{ m.team }} <span class="grey--text ml-1">({{ m.role }})</span>
                          </v-chip>
                        </div>
                        <div v-else class="body-2 grey--text">—</div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Compte card (read-only) -->
                <v-col cols="12" md="6">
                  <v-card elevation="0" class="mb-4 rounded-lg" outlined>
                    <v-card-title class="subtitle-1 font-weight-medium pb-1">
                      <v-icon left small>mdi-shield-account-outline</v-icon>
                      Compte
                    </v-card-title>
                    <v-card-text>
                      <v-switch
                        :input-value="detail.is_active"
                        label="Actif"
                        dense readonly disabled
                        class="mt-0"
                      />
                      <v-switch
                        :input-value="detail.is_staff"
                        label="Staff"
                        dense readonly disabled
                        class="mt-2"
                      />
                      <v-switch
                        :input-value="detail.is_demo"
                        label="Démo"
                        dense readonly disabled
                        class="mt-2"
                      />
                      <div class="caption grey--text">
                        Lecture seule — modifications dans la zone danger ci-dessous.
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Limites card -->
                <v-col cols="12" md="6">
                  <v-card elevation="0" class="mb-4 rounded-lg" outlined>
                    <v-card-title class="subtitle-1 font-weight-medium pb-1">
                      <v-icon left small>mdi-gauge</v-icon>
                      Limites
                    </v-card-title>
                    <v-card-text class="pa-0">
                      <div v-if="!detail.limits.length" class="caption grey--text px-4 py-3">
                        Aucune offre associée à ce compte.
                      </div>
                      <div
                        v-for="offer in detail.limits"
                        :key="offer.slug"
                        class="px-4 py-3"
                        :class="{ 'limit-offer--bordered': offer !== detail.limits[0] }"
                      >
                        <div class="caption grey--text mb-2">{{ offer.name }}</div>
                        <div v-for="(limit, i) in offer.limits" :key="limit.name" :class="{ 'mt-3': i > 0 }">
                          <div class="d-flex align-center mb-1">
                            <span class="caption font-weight-medium flex-grow-1">{{ periodLabel(limit.period) }}</span>
                            <span class="caption" :class="limit.exceeded ? 'red--text text--darken-1' : 'grey--text'">
                              {{ formatCost(limit.current_cost) }} / {{ formatCost(limit.cost_cap) }}
                            </span>
                          </div>
                          <v-progress-linear
                            :value="limit.percentage"
                            :color="limit.exceeded ? 'red darken-1' : limit.percentage >= 90 ? 'orange darken-1' : 'blue darken-1'"
                            background-color="grey lighten-3"
                            rounded height="6"
                          />
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Plans de stock par métier -->
                <v-col cols="12" md="6">
                  <AdminUserStockPlans :user-id="userId" />
                </v-col>

                <!-- Coût & usage card -->
                <v-col cols="12" md="6">
                  <v-card elevation="0" class="mb-4 rounded-lg" outlined>
                    <v-card-title class="subtitle-1 font-weight-medium pb-1">
                      <v-icon left small>mdi-currency-usd</v-icon>
                      Coût &amp; usage
                      <v-spacer />
                      <v-btn-toggle v-model="usagePeriod" dense mandatory>
                        <v-btn v-for="p in periods" :key="p" x-small :value="p">{{ periodLabel(p) }}</v-btn>
                      </v-btn-toggle>
                    </v-card-title>
                    <v-card-text>
                      <div v-if="!currentUsage.by_call_type.length" class="caption grey--text py-2">
                        Aucun usage sur cette période.
                      </div>
                      <div v-else style="display: grid; gap: 6px">
                        <div v-for="row in currentUsage.by_call_type" :key="row.call_type" class="d-flex justify-space-between">
                          <span class="caption grey--text">{{ callTypeLabel(row.call_type) }}</span>
                          <span class="caption font-weight-medium">{{ formatCost(row.cost) }}</span>
                        </div>
                      </div>
                      <v-divider class="my-2" />
                      <div class="d-flex justify-space-between">
                        <span class="body-2 font-weight-medium">Total</span>
                        <span class="body-2 font-weight-bold orange--text text--darken-1">
                          {{ formatCost(currentUsage.total_cost) }}
                        </span>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Zone danger -->
              <v-card elevation="0" class="mb-4 rounded-lg" outlined style="border-color: rgba(255,0,0,0.3) !important">
                <v-card-title class="subtitle-1 font-weight-medium pb-1 error--text">
                  <v-icon left small color="error">mdi-alert-outline</v-icon>
                  Zone danger
                </v-card-title>
                <v-card-text>
                  <v-switch
                    v-model="detail.is_active"
                    label="Actif"
                    dense
                    persistent-hint
                    hint="Désactiver empêche cet utilisateur de se connecter, sans supprimer ses données."
                    class="mt-0"
                    :loading="toggling.is_active"
                    @change="toggleField('is_active')"
                  />
                  <v-switch
                    v-if="isSuperuser"
                    v-model="detail.is_staff"
                    label="Staff"
                    dense
                    persistent-hint
                    hint="Donne accès à l'administration."
                    class="mt-4"
                    :loading="toggling.is_staff"
                    @change="toggleField('is_staff')"
                  />
                  <v-switch
                    v-if="isSuperuser"
                    v-model="detail.is_demo"
                    label="Démo"
                    dense
                    persistent-hint
                    hint="Limite ce compte aux moteurs de démonstration."
                    class="mt-4"
                    :loading="toggling.is_demo"
                    @change="toggleField('is_demo')"
                  />
                </v-card-text>
              </v-card>

              <!-- Tabs -->
              <v-card elevation="0" outlined>
                <v-tabs v-model="tab" background-color="transparent">
                  <v-tab>Conversations</v-tab>
                  <v-tab>Projets</v-tab>
                  <v-tab>Sessions Agent</v-tab>
                  <v-tab>Moteurs personnels</v-tab>
                  <v-tab>Fichiers</v-tab>
                </v-tabs>

                <v-divider />

                <v-tabs-items v-model="tab">
                  <!-- Conversations -->
                  <v-tab-item>
                    <v-data-table
                      :headers="conversationHeaders"
                      :items="detail.conversations"
                      sort-by="updated_at"
                      :sort-desc="true"
                      class="elevation-0"
                      @click:row="item => $router.push({ name: 'chat_detail', params: { id: item.id } })"
                      style="cursor: pointer"
                    >
                      <template v-slot:[`item.updated_at`]="{ item }">{{ formatDate(item.updated_at) }}</template>
                    </v-data-table>
                  </v-tab-item>

                  <!-- Projets -->
                  <v-tab-item>
                    <v-data-table
                      :headers="projectHeaders"
                      :items="detail.projects"
                      sort-by="created_at"
                      :sort-desc="true"
                      class="elevation-0"
                    >
                      <template v-slot:[`item.created_at`]="{ item }">{{ formatDate(item.created_at) }}</template>
                    </v-data-table>
                  </v-tab-item>

                  <!-- Sessions Agent -->
                  <v-tab-item>
                    <v-data-table
                      :headers="agentSessionHeaders"
                      :items="detail.agent_sessions"
                      sort-by="created_at"
                      :sort-desc="true"
                      class="elevation-0"
                      @click:row="item => $router.push({ name: 'admin_agent_session_detail', params: { id: item.id } })"
                      style="cursor: pointer"
                    >
                      <template v-slot:[`item.initial_message`]="{ item }">
                        <span class="caption" style="max-width:320px;display:inline-block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
                          {{ item.initial_message }}
                        </span>
                      </template>
                      <template v-slot:[`item.outcome_type`]="{ item }">
                        <v-chip x-small :color="item.outcome_type === 'project' ? 'primary' : 'teal'" dark v-if="item.outcome_type">
                          {{ item.outcome_type === 'project' ? 'Projet' : 'Conversation' }}
                        </v-chip>
                        <span v-else class="grey--text caption">—</span>
                      </template>
                      <template v-slot:[`item.created_at`]="{ item }">{{ formatDate(item.created_at) }}</template>
                    </v-data-table>
                  </v-tab-item>

                  <!-- Moteurs personnels -->
                  <v-tab-item>
                    <v-data-table
                      :headers="engineHeaders"
                      :items="detail.engines"
                      sort-by="name"
                      class="elevation-0"
                      @click:row="item => $router.push({ name: 'admin_engine_edit', params: { id: item.id } })"
                      style="cursor: pointer"
                    >
                      <template v-slot:[`item.is_active`]="{ item }">
                        <v-icon small :color="item.is_active ? 'success' : 'grey lighten-2'">
                          {{ item.is_active ? 'mdi-check-circle' : 'mdi-minus-circle-outline' }}
                        </v-icon>
                      </template>
                    </v-data-table>
                  </v-tab-item>

                  <!-- Fichiers -->
                  <v-tab-item>
                    <v-data-table
                      :headers="fileHeaders"
                      :items="detail.files"
                      sort-by="uploaded_at"
                      :sort-desc="true"
                      class="elevation-0"
                    >
                      <template v-slot:[`item.uploaded_at`]="{ item }">{{ formatDate(item.uploaded_at) }}</template>
                      <template v-slot:[`item.actions`]="{ item }">
                        <v-btn icon small :href="item.file_url" target="_blank" rel="noopener" :disabled="!item.file_url">
                          <v-icon small>mdi-download-outline</v-icon>
                        </v-btn>
                      </template>
                    </v-data-table>
                  </v-tab-item>
                </v-tabs-items>
              </v-card>

            </template>

            <v-row v-else>
              <v-col cols="12" md="8" offset-md="2">
                <v-alert type="error" outlined>Utilisateur introuvable.</v-alert>
              </v-col>
            </v-row>

          </v-col>
        </v-row>
      </v-container>
    </template>
  </MainLayout>
</template>

<script>
import PageBack from '@/components/ui/PageBack.vue'
import MainLayout from '@/layout/MainLayout.vue'
import { fetchAdminUser, updateAdminUser } from '@/services/adminApi'
import AdminUserStockPlans from '@/components/admin/AdminUserStockPlans.vue'

const PERIOD_LABELS = {
  daily: 'Quotidien',
  weekly: 'Hebdomadaire',
  monthly: 'Mensuel',
  total: 'Total',
}

const CALL_TYPE_LABELS = {
  classifier_stage1: 'Classification Secteur',
  classifier_stage2: 'Classification Interaction',
  sector_qualify: 'Qualification Secteur',
  sector_agent: 'Réponse Secteur',
  sector_brief: 'Génération Brief',
  sector_critic: 'Critique Brief',
  generate_title: 'Génération Titre',
  lookup: 'Recherche Catalogue',
  direct_chat: 'Chat Direct',
  qa_analysis: 'Analyse QA',
}

export default {
  name: 'AdminUserDetailView',
  components: { PageBack, MainLayout, AdminUserStockPlans },

  data() {
    return {
      loading: false,
      error: null,
      detail: null,
      tab: 0,
      usagePeriod: 'monthly',
      periods: ['daily', 'weekly', 'monthly', 'total'],
      toggling: { is_active: false, is_staff: false, is_demo: false },

      conversationHeaders: [
        { text: 'Titre', value: 'title' },
        { text: 'Moteur', value: 'engine' },
        { text: 'Messages', value: 'message_count', align: 'right' },
        { text: 'Modifié', value: 'updated_at' },
      ],
      projectHeaders: [
        { text: 'Titre', value: 'title' },
        { text: 'Domaine', value: 'domain' },
        { text: 'Statut', value: 'status' },
        { text: 'Créé', value: 'created_at' },
      ],
      agentSessionHeaders: [
        { text: 'Message initial', value: 'initial_message', sortable: false },
        { text: 'Stage 1', value: 'stage1' },
        { text: 'Stage 2', value: 'stage2' },
        { text: 'Résultat', value: 'outcome_type' },
        { text: 'Créé', value: 'created_at' },
      ],
      engineHeaders: [
        { text: 'Nom', value: 'name' },
        { text: 'Fournisseur', value: 'provider_slug' },
        { text: 'Modèle', value: 'model' },
        { text: 'Actif', value: 'is_active', align: 'center' },
      ],
      fileHeaders: [
        { text: 'Nom', value: 'name' },
        { text: 'Type', value: 'file_type' },
        { text: 'Ajouté le', value: 'uploaded_at' },
        { text: '', value: 'actions', sortable: false, align: 'right' },
      ],
    }
  },

  computed: {
    isSuperuser() {
      return this.$store.getters['settings/isSuperuser']
    },

    userId() {
      return this.$route.params.id
    },

    fullName() {
      if (!this.detail) return ''
      return [this.detail.first_name, this.detail.last_name].filter(Boolean).join(' ')
    },

    initials() {
      if (!this.detail) return '?'
      const source = this.fullName || this.detail.username || '?'
      return source
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(w => w[0].toUpperCase())
        .join('')
    },

    currentUsage() {
      return (this.detail && this.detail.usage_summary[this.usagePeriod]) || { total_cost: '0', by_call_type: [] }
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        this.detail = await fetchAdminUser(this.userId)
      } catch {
        this.error = 'Impossible de charger cet utilisateur.'
      } finally {
        this.loading = false
      }
    },

    async toggleField(field) {
      this.toggling[field] = true
      try {
        await updateAdminUser(this.userId, { [field]: this.detail[field] })
      } catch {
        this.detail[field] = !this.detail[field]
        this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: 'Erreur lors de la mise à jour.' })
      } finally {
        this.toggling[field] = false
      }
    },

    periodLabel(period) {
      return PERIOD_LABELS[period] || period
    },

    callTypeLabel(callType) {
      return CALL_TYPE_LABELS[callType] || callType
    },

    formatDate(iso) {
      if (!iso) return '—'
      return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })
    },

    formatCost(value) {
      const n = parseFloat(value)
      if (isNaN(n)) return '—'
      return `$${n.toFixed(4)}`
    },
  },
}
</script>

<style scoped>
.limit-offer--bordered {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
